import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, TriangleAlert } from "lucide-react";
import { Flash } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const FlashResetDialog = ({ open, onOpenChange, onDone }) => {
  const [confirm, setConfirm] = useState("");
  const [notes, setNotes] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setConfirm(""); setNotes(true); } }, [open]);

  const reset = async () => {
    setBusy(true);
    try {
      const r = await Flash.reset(notes);
      toast.success(`Cleared ${r.entries_deleted} entries${notes ? ` and ${r.notes_deleted} notes` : ""}. Fresh start!`);
      onDone();
      onOpenChange(false);
    } catch {
      toast.error("Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white" data-testid="flash-reset-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-rose-700">
            <TriangleAlert className="w-5 h-5" /> Reset all flash data
          </DialogTitle>
          <DialogDescription>
            Deletes every percentage you've entered for every date. Your forms, owners, goals, order and hospital profile are kept. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox id="reset-notes" checked={notes} onCheckedChange={(v) => setNotes(!!v)} data-testid="reset-include-notes" />
            <Label htmlFor="reset-notes" className="text-sm">Also delete all owner notes</Label>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Type <span className="font-mono font-bold">RESET</span> to confirm</Label>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="font-mono" data-testid="reset-confirm-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={reset} disabled={confirm !== "RESET" || busy} className="bg-rose-600 hover:bg-rose-700" data-testid="reset-confirm-button">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Clear all data
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
